import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Edit, PlusIcon, Trash2, Users } from "lucide-react";
import { shoppingListApi } from "@/services/shoppingListApi";
import { ShoppingList } from "@/pages/ShoppingList/types.ts";
import { CreateListModal } from "./components/CreateListModal";
import { EditListModal } from "./components/EditListModal";
import { showToast } from "@/components/tools/toast.ts";
import { ShareManagementDialog } from "@/components/layout/ShareManagementDialog.tsx";
import { getUserLogged } from "@/utils/axios/userUtils.ts";

export const ShoppingListPage = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // États distincts pour chaque modale et liste sélectionnée
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [selectedList, setSelectedList] = useState<ShoppingList | null>(null);

  // Mutation pour partager une liste
  const shareListMutation = useMutation({
    mutationFn: ({ listId, userIds }: { listId: string; userIds: string[] }) =>
      shoppingListApi.shareList(listId, userIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shopping-lists"] });
      showToast("success", "Liste partagée avec succès!");
      setIsShareModalOpen(false);
    },
    onError: (error) => {
      let errorMsg = "Erreur lors du partage de la liste";
      if (error instanceof Error) errorMsg += `: ${error.message}`;
      showToast("error", errorMsg);
    },
  });

  // Récupérer toutes les listes de courses
  const {
    data: lists = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["shopping-lists"],
    queryFn: shoppingListApi.getLists,
  });

  // Gérer la navigation vers une liste spécifique
  const handleListClick = (listId: string) => {
    navigate({ to: `/house/shopping-list/${listId}` });
  };

  // Handlers pour chaque action - chacun avec sa propre responsabilité
  const handleEditClick = (e: React.MouseEvent, list: ShoppingList) => {
    e.stopPropagation(); // Empêche la navigation vers la liste
    e.preventDefault();
    setSelectedList(list);
    setIsEditModalOpen(true);
  };

  const handleShareClick = (e: React.MouseEvent, list: ShoppingList) => {
    e.stopPropagation(); // Empêche la navigation vers la liste
    e.preventDefault();
    setSelectedList(list);
    setIsShareModalOpen(true);
  };

  const handleDeleteClick = async (e: React.MouseEvent, list: ShoppingList) => {
    e.stopPropagation(); // Empêche la navigation vers la liste
    e.preventDefault();
    if (list.owner !== getUserLogged()?._id) {
      showToast(
        "error",
        "Vous ne pouvez pas supprimer une liste dont vous n'êtes pas le propriétaire."
      );
      return;
    }
    // Confirmation avant suppression
    if (
      !window.confirm(
        `Êtes-vous sûr de vouloir supprimer la liste "${list.name}" ? Cette action est irréversible.`
      )
    ) {
      return; // Annulation si l'utilisateur refuse
    }

    try {
      await shoppingListApi.deleteList(list._id);
      await queryClient.invalidateQueries({ queryKey: ["shopping-lists"] });
      showToast("info", `Liste "${list.name}" supprimée avec succès!`);
    } catch (error) {
      let errorMsg = "Erreur lors de la suppression de la liste";
      if (error instanceof Error) errorMsg += `: ${error.message}`;
      showToast("error", errorMsg);
    }
  };

  // Fermer les modales - chacune avec sa propre fonction
  const closeEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedList(null);
  };

  const closeShareModal = () => {
    setIsShareModalOpen(false);
    setSelectedList(null);
  };

  // Gérer la confirmation du partage
  const handleShareConfirm = (userIds: string[]) => {
    if (!selectedList) return;
    shareListMutation.mutate({ listId: selectedList._id, userIds });
  };

  // Calculer le pourcentage de complétion d'une liste
  const calculateProgress = (list: ShoppingList): number => {
    const totalItems = list.items.length;
    if (totalItems === 0) return 0;

    const completedItems = list.items.filter((item) => item.checked).length;
    return Math.round((completedItems / totalItems) * 100);
  };

  // Déterminer la couleur de la barre de progression
  const getProgressColor = (progress: number): string => {
    if (progress < 30) return "progress-error";
    if (progress < 70) return "progress-warning";
    return "progress-success";
  };

  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Mes Listes de Courses</h1>
        <button
          className="btn btn-primary"
          onClick={() => setIsCreateModalOpen(true)}
        >
          <PlusIcon size={20} />
          Nouvelle Liste
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center my-12">
          <span className="loading loading-spinner loading-lg"></span>
        </div>
      ) : isError ? (
        <div className="alert alert-error">
          Erreur lors du chargement des listes. Veuillez réessayer.
        </div>
      ) : lists.length === 0 ? (
        <div className="card bg-base-100 shadow-xl text-center p-8">
          <h3 className="text-xl mb-4">
            Vous n'avez pas encore de listes de courses
          </h3>
          <p className="mb-6">
            Créez votre première liste en cliquant sur le bouton ci-dessus.
          </p>
          <button
            className="btn btn-primary mx-auto"
            onClick={() => setIsCreateModalOpen(true)}
          >
            Créer ma première liste
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {lists.map((list) => (
            <div
              key={list._id}
              className="card bg-base-100 shadow-xl hover:shadow-2xl transition-shadow cursor-pointer"
              onClick={() => handleListClick(list._id)}
            >
              <div className="card-body">
                <div className="flex justify-between items-start">
                  <h2 className="card-title">{list.name}</h2>
                  <div className="badge badge-outline">{list.type}</div>
                </div>

                <div className="mt-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span>
                      {list.items.filter((item) => item.checked).length} sur{" "}
                      {list.items.length} items
                    </span>
                    <span>{calculateProgress(list)}%</span>
                  </div>
                  <progress
                    className={`progress w-full ${getProgressColor(
                      calculateProgress(list)
                    )}`}
                    value={calculateProgress(list)}
                    max="100"
                  ></progress>
                </div>

                <div className="mt-4 text-sm text-gray-500">
                  <div className="flex justify-between">
                    <span>
                      Partagée avec: {list.sharedWith?.length || 0} personne(s)
                    </span>
                    <span>
                      Mise à jour:{" "}
                      {new Date(list.updatedAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>

                {/* Boutons d'action */}
                <div className="card-actions justify-end mt-4">
                  <button
                    type="button"
                    className="btn btn-sm btn-circle btn-ghost hover:bg-base-300"
                    onClick={(e) => handleEditClick(e, list)}
                    aria-label="Éditer"
                  >
                    <Edit size={16} className="text-gray-600" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-circle btn-ghost hover:bg-indigo-100"
                    onClick={(e) => handleShareClick(e, list)}
                    aria-label="Partager"
                  >
                    <Users size={16} className="text-indigo-500" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-circle btn-ghost hover:bg-red-100"
                    onClick={(e) => handleDeleteClick(e, list)}
                    aria-label="Supprimer"
                  >
                    <Trash2 size={16} className="text-red-500" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Chaque modale a son propre état et est rendue indépendamment */}

      {/* Modal pour créer une nouvelle liste */}
      <CreateListModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          setIsCreateModalOpen(false);
          refetch();
        }}
      />

      {/* Modal pour éditer une liste */}
      {selectedList && isEditModalOpen && (
        <EditListModal
          isOpen={true}
          onClose={closeEditModal}
          list={selectedList}
        />
      )}

      {/* Modal de partage - maintenant complètement indépendante */}
      {selectedList && isShareModalOpen && (
        <ShareManagementDialog
          key={`share-dialog-${selectedList._id}`}
          item={selectedList}
          isOpen={true}
          onClose={closeShareModal}
          onConfirm={handleShareConfirm}
          isLoading={shareListMutation.isPending}
          dialogTitle={`Partager "${selectedList.name}"`}
        />
      )}
    </div>
  );
};
